import dayjs, { Dayjs, extend } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { countries, countryCode, langDateMap, langDateTimeMap, roles } from './constants';

extend(utc);
extend(timezone);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayName = (person: any, skipComma?: boolean) => {
  if (!person) return '';
  return person?.last_name
    ? `${person?.last_name?.trim()}${
        person?.first_name ? (skipComma ? ' ' : ', ') + person?.first_name?.trim() : ''
      }`
    : `${
        person?.first_name
          ? person?.first_name?.trim()
          : skipComma
            ? person?.email?.replaceAll('.', '')?.split('@')[0]?.trim()
            : person?.email?.trim()
      }`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const dateFormat: any = () => {
  try {
    return langDateMap[window.navigator.language] ?? langDateMap.default;
  } catch (e) {
    return langDateMap.default;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayTime = (value: any) => {
  // 🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴🔴
  // This should not be a try catch block for me
  // When dayjs receive a invalid argument return the string 'Invalid Date'
  try {
    return dayjs.utc(value).utcOffset(0).format('hh:mm A');
  } catch (e) {
    return '';
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayDate = (value: any) => {
  try {
    const dateFormat = langDateMap[window.navigator.language] ?? langDateMap.default;
    return dayjs.utc(value).utcOffset(0).format(dateFormat);
  } catch (e) {
    return dayjs.utc(value).utcOffset(0).format(langDateMap.default);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayDateWithLocalOffset = (value: any) => {
  try {
    const dateFormat = langDateMap[window.navigator.language] ?? langDateMap.default;
    return dayjs.utc(value).utcOffset(getMyUtc()).format(dateFormat);
  } catch (e) {
    return dayjs.utc(value).utcOffset(getMyUtc()).format(langDateMap.default);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayDateTime = (value: any) => {
  try {
    const dateFormat = langDateTimeMap[window.navigator.language] ?? langDateTimeMap.default;
    return dayjs.utc(value).utcOffset(0).format(dateFormat);
  } catch (e) {
    return dayjs.utc(value).utcOffset(0).format(langDateTimeMap.default);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayTimeWithOffset = (value: any, offset: any) => {
  try {
    return dayjs.utc(value).utcOffset(offset).format('hh:mm A');
  } catch (e) {
    return '';
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayDateWithOffset = (value: any, offset: any) => {
  try {
    const dateFormat = langDateMap[window.navigator.language] ?? langDateMap.default;
    return dayjs.utc(value).utcOffset(offset).format(dateFormat);
  } catch (e) {
    return dayjs.utc(value).utcOffset(offset).format(langDateMap.default);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const displayDateTimeWithOffset = (value: any, offset: any) => {
  try {
    const dateFormat = langDateTimeMap[window.navigator.language] ?? langDateTimeMap.default;
    return dayjs.utc(value).utcOffset(offset).format(dateFormat);
  } catch (e) {
    return dayjs.utc(value).utcOffset(offset).format(langDateTimeMap.default);
  }
};

export const formatDateToSave = (value: Date | Dayjs | string) => dayjs(value).format('YYYY-MM-DD');

export const formatDateTimeToSave = (value: Date | Dayjs) =>
  dayjs(value).format('YYYY-MM-DDTHH:mm:ss.000ZZ');

export const datePickerFormat = (value: Date | Dayjs | string) => {
  return dayjs.utc(value).utcOffset(0);
};

export const disableFutureDate = (current: Dayjs) => {
  // Cannot select days after today and today
  return current && current > dayjs().endOf('day');
};

export const getDefaultDOB = () => {
  return dayjs().subtract(22, 'year');
};

export const codeToCountry = (code?: string) => {
  if (!code) return '-';
  const country = countries.find((el) => el.value === code)?.label;
  return country ?? code;
};

export const codeToDailCode = (code?: string) => {
  if (!code) return '';
  return countryCode.find((el) => el.code === code)?.dialCode;
};

export const getMyUtc = () => {
  return dayjs().utcOffset();
};

export const transformImageToUpload = async (file: Blob) => {
  try {
    const filePromise = new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        return resolve(e.target?.result);
      };
    });
    const imageData = await filePromise;
    return imageData ? imageData.toString() : '';
  } catch (e) {
    return '';
  }
};

export const getRoleLabels = (myroles?: string[]) => {
  return roles?.filter((role) => myroles?.includes(role.value));
};

export const inchToFeetInchString = (totalInches: number): string => {
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  const inchesDisplay = Math.round(inches);

  return `${feet} ft, ${inchesDisplay} in`;
};

export const inchToFeet = (h?: number) => {
  const height = h || 0;
  return Math.floor(height / 12);
};

export const inchToFeetInch = (h?: number) => {
  const height = h || 0;
  const ft = Math.floor(height / 12);
  // Keep decimals as-is, don't round
  let remainingInches = height - ft * 12;

  // Handle case where value is 12 or more
  if (remainingInches >= 12) {
    remainingInches = 0;
  }

  return remainingInches;
};

export const FeetToInch = (f?: number, i?: number) => {
  const feet = f || 0;
  const inch = i || 0;
  return feet * 12 + inch;
};

// Array.from(new Set(units.filter(el=>el?.address?.country)?.map(el=>el?.address?.country?.toLowerCase())))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mediaDownload = async (fileURL: any): Promise<string> => {
  try {
    // eslint-disable-next-line compat/compat
    const res = await fetch(fileURL);
    const blob = await res.blob();
    // eslint-disable-next-line compat/compat
    return window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  } catch (e) {
    // console.log(e);
    return fileURL;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const downloadPDF = (pdf: any, filename: string) => {
  const linkSource = `data:application/pdf;base64,${pdf}`;
  const downloadLink = document.createElement('a');
  const fileName = `${filename}`;
  downloadLink.href = linkSource;
  downloadLink.target = '_blank';
  downloadLink.download = fileName;
  downloadLink.click();
};

export const convertToKB = (bytes: number) => {
  const kilobytes = bytes / 1024;
  return kilobytes.toFixed(2);
};
